"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Compass,
  Sparkles,
  Crown,
  Stethoscope,
  Lock,
  Eye,
  Brain,
  Building2,
  Trophy,
  ArrowRight,
  Award,
  GitBranch,
  Users,
  Target,
  Rocket,
  Flag,
  BookOpen,
  Globe2,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { ArchitectureDiagramBlock } from "@/lib/blocks/architecture-diagram-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";

const CAREER_CENTER = {
  label: "Dr. Jyoti Kush",
  body: "The operational constant across every phase. Co-builder. COO. The clinical method applied at sovereign scale.",
  icon: <Compass className="size-6" />,
};

const CAREER_SATELLITES = [
  { label: "Medical Foundation", body: "MBBS Gynaecology · Delhi. The operating system installation.", icon: <Stethoscope className="size-4" />, accent: "red" as const },
  { label: "Phase 01 · Privacy", body: "Encrypted networks for sovereign clients. Zero security incidents.", icon: <Lock className="size-4" />, accent: "purple" as const },
  { label: "Phase 02 · Intelligence", body: "Eighty-nine percent prediction accuracy. Two hundred-plus platforms monitored.", icon: <Eye className="size-4" />, accent: "cyan" as const },
  { label: "Phase 03 · Perception", body: "The Penta-P framework. Country-level political campaigns.", icon: <Brain className="size-4" />, accent: "emerald" as const },
  { label: "Phase 04 · Conglomerate", body: "MaxiMize Infinium. Nine platforms. Eighteen countries.", icon: <Building2 className="size-4" />, accent: "amber-gold" as const },
  { label: "The 70% Project", body: "Decade of evidence for women in operational leadership.", icon: <Users className="size-4" />, accent: "denim" as const },
  { label: "Prestige Stages", body: "Nobel Peace Prize. World Economic Forum presence.", icon: <Trophy className="size-4" />, accent: "red" as const },
  { label: "Operator's Notebook", body: "Biweekly publication. One principle, made usable.", icon: <BookOpen className="size-4" />, accent: "purple" as const },
];

const CAREER_CONNECTIONS = [
  { from: -1, to: 0 }, { from: -1, to: 1 }, { from: -1, to: 2 }, { from: -1, to: 3 },
  { from: -1, to: 4 }, { from: -1, to: 5 }, { from: -1, to: 6 }, { from: -1, to: 7 },
];

const SCALING_PILLARS = [
  { num: "01", title: "Protocol with Judgment", body: "The five-step methodology — Discovery, Research, Monitor, Analysis, Execution — provided the universal protocol. Judgment was applied by operators who understood local context, cultural nuances, and political dynamics.", accent: "red" as const },
  { num: "02", title: "Cross-Cultural Fluency", body: "Teams spanning fifteen-plus languages across seven primary regions. The cultural intelligence embedded in the operating method is what enables sovereign-grade delivery in environments where a single cultural misstep can compromise an entire engagement.", accent: "purple" as const },
  { num: "03", title: "Platform Orchestration", body: "The neural command interface orchestrated across all platforms and geographies. Ninety-five percent coordination success rate. Information flowed only to those authorized, with compartmentalization essential for sovereign-grade security.", accent: "cyan" as const },
  { num: "04", title: "Penta-P Coherence", body: "Five domains operating as a closed-loop system where intelligence from one domain strengthens execution in every other. The framework scaled because each domain was self-reinforcing — the more domains operating, the stronger each became.", accent: "emerald" as const },
];

const TEAM_EVOLUTION = [
  { title: "Foundational Selection", body: "Small, focused teams selected for the only quality that mattered — the ability to secure sovereign-grade communications without a single breach. Precision. Protocol adherence. Accountability. The ability to function under absolute pressure.", accent: "red" as const },
  { title: "Multidisciplinary Expansion", body: "As the organization expanded from security into intelligence, perception, governance, and politics, the team composition expanded to include political analysts, data scientists, security experts, developers, governance specialists, and communications professionals.", accent: "purple" as const },
  { title: "The 70% Emergence", body: "The seventy percent figure emerged not from policy but from performance. Dr. Kush optimized for the operating criteria — rigorously, blind to everything else — and the teams kept coming back majority-women. The number is a consequence, not a target.", accent: "cyan" as const },
  { title: "Structural Advantages", body: "Communication improved. Risk discipline strengthened. Collaboration replaced competition. Retention improved. Ego friction decreased. The advantages are not anecdotal — they are structural.", accent: "emerald" as const },
  { title: "Institutional Multiplier", body: "Retention became an operational multiplier, not a human resources metric. Sovereign-scale operations depend on institutional knowledge — understanding client histories, platform architectures, operational protocols.", accent: "amber-gold" as const },
  { title: "The Documented Evidence", body: "Fifteen victories out of eighteen campaigns. Over three hundred elite clients served. Zero security incidents. The world is mis-allocating talent by selecting on the wrong signals. A decade of arbitraging that error.", accent: "denim" as const },
];

const PHASES = [
  {
    num: "01",
    period: "Years 1–3",
    title: "Information Privacy",
    subtitle: "Building the sovereign-grade security foundation.",
    body: "The first phase was defined by a single imperative: build the trust that makes everything else possible. Encrypted private networks for governments and their highest offices — the kind of work where a single vulnerability can compromise national stability. Zero security incidents across every engagement. The cultural DNA of sovereign-grade security established here fueled every subsequent expansion.",
    accent: "red",
    icon: Lock,
    outputs: ["Encrypted private networks", "Zero-breach track record", "Sovereign-grade cultural DNA"],
  },
  {
    num: "02",
    period: "Years 3–5",
    title: "Intelligence Evolution",
    subtitle: "The emergence of foresight.",
    body: "The driving realization: security without intelligence is blind. As government clients demanded broader capabilities, the organization evolved from defensive security into network monitoring, digital listening, and intelligence analysis. The transition from defensive security to proactive intelligence gave the organization foresight — the ability to see not just what was happening, but what was about to happen. Eighty-nine percent prediction accuracy was sustained through continuous refinement.",
    accent: "purple",
    icon: Eye,
    outputs: ["Network monitoring", "Digital listening", "Predictive intelligence"],
  },
  {
    num: "03",
    period: "Years 5–7",
    title: "Perception Expansion",
    subtitle: "Broadening into governance and political strategy.",
    body: "The third phase began with a recognition that raw intelligence alone was insufficient. A government with perfect operational security could still lose an election to a viral narrative. Privacy without perception is vulnerability. Existing clients invited the organization to apply methodologies to perception management, governance support, and country-level political campaigns. The Penta-P framework crystallized here as a closed-loop system.",
    accent: "cyan",
    icon: Brain,
    outputs: ["Perception management", "Governance support", "Penta-P framework"],
  },
  {
    num: "04",
    period: "Years 7–10",
    title: "The Digital Conglomerate",
    subtitle: "The evolution into MaxiMize Infinium.",
    body: "The fourth and culminating phase was not a strategic decision. It was a recognition of what the organization had already become. MaxiMize Infinium — the World&apos;s First Digital Conglomerate for Strategic Sovereignty. Sixty-plus integrated services across five interconnected domains. Eighteen countries. Three continents. Over three hundred elite clients served. And it grew, excelled, and evolved even during the COVID era — when most organizations contracted.",
    accent: "emerald",
    icon: Building2,
    outputs: ["Digital Conglomerate", "60+ integrated services", "18 countries, 3 continents"],
  },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
};

const MILESTONES = [
  { year: "Foundation", title: "Medical Training Complete", body: "MBBS with specialization in Gynaecology from Delhi. The installation of the operating system. The six principles drilled into muscle memory.", icon: Stethoscope, accent: "red" },
  { year: "Phase 1", title: "First Sovereign Security Engagement", body: "The transition from clinical practice to securing mission-critical infrastructure for government clients. The clinical method translated to sovereign-grade technology. Zero security incidents — the standard that would hold across fifteen-plus years.", icon: Lock, accent: "purple" },
  { year: "Phase 2", title: "Predictive Intelligence Emerges", body: "The evolution from defensive security to proactive intelligence. The gain of organizational foresight — the ability to see not just what was happening, but what was about to happen.", icon: Eye, accent: "cyan" },
  { year: "Phase 3", title: "Perception & Governance Expansion", body: "The expansion into perception management, governance support, and political strategy. The completion of the intelligence-perception-security triad. The recognition that raw intelligence without perception is incomplete.", icon: Brain, accent: "emerald" },
  { year: "Phase 3", title: "First Campaign Victory", body: "The entry into political consulting and the first verified electoral outcome. The beginning of a record that would reach fifteen victories out of eighteen engagements — an eighty-three point three percent success rate.", icon: Trophy, accent: "amber-gold" },
  { year: "Phase 3", title: "India — The Crucible", body: "The deployment across nine hundred million voters, twenty-two official languages, and hundreds of thousands of polling booths. The validation of the complete integrated system — digital intelligence confirmed against physical ground truth.", icon: Flag, accent: "denim" },
  { year: "Phase 4", title: "Digital Conglomerate Formation", body: "The recognition that the organization had evolved beyond its original identity. The World&apos;s First Digital Conglomerate for Strategic Sovereignty. Sixty-plus services. Eighteen countries. Three continents.", icon: Building2, accent: "purple" },
  { year: "Phase 4", title: "The 70% Project Launched", body: "The documentation of the operating case for women in leadership. Not a diversity initiative — a talent strategy with a decade of evidence. The public expression of a principle that had been proving itself across every engagement.", icon: Users, accent: "red" },
];

const PRINCIPLES = [
  { num: "01", title: "Duration is the proof of excellence.", body: "Consultants advise and move on. Builders stay, scale, and sustain. The decade of building is the evidence that operational excellence has been achieved.", accent: "red" },
  { num: "02", title: "The operating system scales.", body: "Diagnose. Triage. Run the protocol. Keep the judgment. Own the outcome. Do no harm. The six principles proven across eighteen countries and the integrated platform ecosystem.", accent: "purple" },
  { num: "03", title: "The pivot was a domain change, not a career change.", body: "The same operating discipline, applied to a different class of problem. The physician&apos;s method became the operator&apos;s method for securing sovereign-grade systems.", accent: "cyan" },
  { num: "04", title: "COVID resilience was not accidental.", body: "Decentralized teams, encrypted communications, sovereign-grade security, and the clinical discipline of operating under pressure. The systems were built to function regardless of external conditions.", accent: "emerald" },
];

export default function JourneyPage() {
  return (
    <>
      <PageHero
        icon={Compass}
        overline="The Journey"
        overlineClassName="text-purple"
        title={
          <>
            From the Delivery Room to <span className="text-gradient-sovereign">Sovereign Scale.</span>
          </>
        }
        subtitle="The career timeline of a physician who built something the world had never seen. Every phase. Every pivot. Every milestone. Every principle forged along the way."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "About", href: "/about/" }, { label: "Journey" }]}
        badge="Four phases · One operational constant"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Executive Summary</span>
          </div>
          <h2 className="text-display-md mb-5">The arc of a career that defies convention.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            From the foundation laid in medical school — where the operating system for the mind was installed — through the clinical years that forged credibility under fire, the pivot into information privacy that extended the clinical method into technology, and the decade of building that evolved a security-focused entity into a full-spectrum Digital Conglomerate. The journey is documented in four defining phases.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The career is not a sequence of job titles. It is the installation and expansion of an operating system — one that began in a delivery room and now governs operations at the level where the stakes are national, the clients are sovereign, and the margin for error is zero.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Compass className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Career Path Map</span>
          </div>
          <h2 className="text-display-md mb-4">Eight satellites around the operational constant.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The career is not a linear sequence. It is an architecture — the operator at the center, with eight capability domains radiating outward. Every domain reinforces every other.
          </p>
        </div>
        <div className="overflow-x-auto">
          <ArchitectureDiagramBlock center={CAREER_CENTER} satellites={CAREER_SATELLITES} connections={CAREER_CONNECTIONS} />
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <GitBranch className="size-3.5 text-red" />
            <span className="text-overline text-red">The Four Phases</span>
          </div>
          <h2 className="text-display-md mb-4">Four phases. One operational constant.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each phase demanded new capabilities, larger teams, and more complex operational coordination. Dr. Jyoti Kush was the operational constant across all of them.
          </p>
        </div>
        <div className="space-y-5">
          {PHASES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group relative glass-card rounded-2xl p-6 lg:p-8 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-40 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="grid lg:grid-cols-[auto_1fr] gap-6 items-start mb-5">
                    <div className="flex items-center gap-4">
                      <div className={`size-16 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring}`}>
                        <p.icon className="size-7 text-white" />
                      </div>
                      <div>
                        <div className={`text-overline ${a.text}`}>Phase {p.num} · {p.period}</div>
                        <h3 className={`text-display-sm ${a.text} leading-tight`}>{p.title}</h3>
                      </div>
                    </div>
                  </div>
                  <p className="text-overline text-muted-foreground mb-4">{p.subtitle}</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-5">{p.body}</p>
                  <div className="flex flex-wrap gap-2">
                    {p.outputs.map((o) => (
                      <span
                        key={o}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-caption font-semibold ${a.text} bg-current/5 border ${a.border}`}
                      >
                        <Target className="size-3" />
                        {o}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Trophy className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Key Milestones</span>
          </div>
          <h2 className="text-display-md mb-4">The landmarks that defined the career.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Moments where the operating system proved itself at a new scale, in a new domain, under new pressures.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {MILESTONES.map((m, i) => {
            const a = ACCENT[m.accent];
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <m.icon className={`size-4 ${a.text}`} />
                  <span className={`text-overline ${a.text}`}>{m.year}</span>
                </div>
                <h3 className={`text-heading-sm mb-2 ${a.text}`}>{m.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{m.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Globe2 className="size-3.5 text-red" />
            <span className="text-overline text-red">The Scaling Challenge</span>
          </div>
          <h2 className="text-display-md mb-4">How the operating system adapted to sovereign scale.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Scaling from a single-country security operation to an eighteen-country Digital Conglomerate is not a matter of hiring more people and opening more offices. It is a fundamental challenge of operational architecture.
          </p>
        </div>
        <PillarGridBlock pillars={SCALING_PILLARS} columns={2} variant="numbered" />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Users className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Team Building Evolution</span>
          </div>
          <h2 className="text-display-md mb-4">From small team to seventy percent women workforce.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The team building journey is not a human resources narrative. It is an operational evolution — the discovery and documentation of a talent strategy that produces the most reliable outcomes at sovereign scale.
          </p>
        </div>
        <CardGridBlock items={TEAM_EVOLUTION} columns={3} variant="icon" />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Sparkles className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Operating Principles</span>
          </div>
          <h2 className="text-display-md mb-4">What the journey reveals.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four operating principles that the decade of building has proven at sovereign scale. These are not values to be discussed — they are protocols to be executed.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {PRINCIPLES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${a.ring} shrink-0`}>
                    <span className="text-heading-md font-bold text-white">{p.num}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-5`}>
            <Sparkles className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Lessons Learned</span>
          </div>
          <h2 className="text-display-md mb-4">What the journey reveals.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Every journey produces lessons. The journey of Dr. Jyoti Kush has produced lessons that are not theoretical — they are extracted from a decade of sovereign-scale operations where the consequences of failure are national and the rewards of success are verified outcomes. Excellence is a system, not a value. If the operating steps cannot be written down, the outcome is luck, not excellence. The clinical method scales. The best operators are the ones the world overlooks. Building in the dark produces sovereign-level trust. Diagnosis before treatment prevents organizational misdiagnosis. Good governance wins elections.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The road ahead is not a departure from what has been built. It is the expansion of what has been proven — the scaling of an operating system from eighteen countries to the world, from sovereign clients to any organization that recognizes the gap between ambition and execution. The Create-Achieve-Innovate cycle never stops. From small to large. From near to distant. From imaginable to unimaginable.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Rocket className="size-10 text-purple mx-auto mb-4" />
            <h2 className="text-display-md mb-3">From the journey, the foundation.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The four phases built the foundation. The credentials, the recognition, and the philosophy document what that foundation makes possible.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/about/credentials/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Award className="size-4" />
                View Credentials
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/biography/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Stethoscope className="size-4" />
                Read the Biography
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "About Dr. Jyoti Kush", description: "The full story. Physician. Operator. Architect of sovereign-scale operations.", href: "/about/", icon: Crown, accent: "red" },
          { title: "Biography", description: "The complete biography — from medical training to operational command.", href: "/about/biography/", icon: Stethoscope, accent: "purple" },
          { title: "Recognition", description: "Nobel Peace Prize, WEF, and sovereign stages.", href: "/about/recognition/", icon: Trophy, accent: "amber-gold" },
          { title: "Philosophy", description: "The clinical method applied to organizations.", href: "/about/philosophy/", icon: Brain, accent: "cyan" },
        ]}
      />
    </>
  );
}
